import InfoIcon from "@mui/icons-material/Info";
import ViewInArIcon from '@mui/icons-material/ViewInAr';
import { Autocomplete } from "@mui/material";
import { SortingState } from "@tanstack/react-table";
import { WarehouseAxios } from "api/WarehouseAxios";
import BootstrapTextField from "Common/BootstrapTextField";
import ActionDropdown from "Common/Components/ActionComponent/ActionDropdown";
import WarehouseTableContainer from "Common/WarehouseTableContainer";
import Colors from "Constants/Color";
import CustomStyle from "Constants/Style";
import { app_modules } from "helpers/apps_modules_list";
import React, {
  Fragment,
  useEffect,
  useMemo,
  useState
} from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import { setSelectedWarehouse } from "slices/Dropdowns/Warehouse/WarehouseDropdown/reducer";
import { fetchWarehouseDropdown } from "slices/Dropdowns/Warehouse/WarehouseDropdown/thunk";
import {
  fetchTransferRequest as OnfetchTransferRequest,
  exportTransferRequestCSV
} from "slices/Warehouse/ProjectWarehouse/TransferRequest/thunk";

const TransferRequest: React.FC = () => {
  document.title = "Transfer Request List";
  const dispatch = useDispatch<any>();

  const selectTransferHistorys = createSelector(
    (state: any) => state.TransferRequest,
    (pkg) => ({
      TransferHistorys: pkg.list,
      rowCount: pkg.rowCount,
      loading: pkg.loading,
      error: pkg.error,
    })
  );

  const { TransferHistorys, rowCount, loading, error } = useSelector(
    selectTransferHistorys
  );

  const user = useSelector((state: any) => state.Login.user);
  const user_permissions = user?.data?.permissions || user?.permissions || [];
  const warehouse_permissions = user_permissions?.find(
    (item: any) => item?.apps_name === app_modules?.warehouse?.app_name
  );

  const project_warehouse_permissions =
    warehouse_permissions?.modules_permissions?.find(
      (item: any) =>
        item?.modules_name ===
        app_modules?.warehouse?.modules?.stock_overview
    );

  const { warehouseDropdownList, warehouseDropdownLoading, selectedWarehouse } =
    useSelector(
      createSelector(
        (state: any) => state.warehouseDropdownReducer,
        (data) => ({
          warehouseDropdownList: data.list,
          warehouseDropdownLoading: data.loading,
          warehouseDropdownError: data.error,
          selectedWarehouse: data.selected,
        })
      )
    );

  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [colValue, setColValue] = useState<any>();
  const [totalRecords, setTotalRecords] = useState(rowCount);

  const [editTransferHistory, setEditTransferHistory] =
    useState<boolean>(false);
  const [columnFilters, setColumnFilters] = useState<any>([]);
  const [filterValue, setFilterValue] = useState<any[]>([]);
  const [edit, setEdit] = useState<any>();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(
      OnfetchTransferRequest({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
        wharehouse_id: selectedWarehouse?.warehouse?.warehouse_id,
      })
    );
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    selectedWarehouse?.warehouse?.warehouse_id,
    warehouseDropdownList,
  ]);

  useEffect(() => {
    setPageIndex(0);
  }, [sorting, pageSize, columnFilters]);

  useEffect(() => {
    setData(TransferHistorys);
  }, [TransferHistorys]);
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      // setSearch(searchInput);
      setPageIndex(0);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);
  useEffect(() => {
    if (selectedWarehouse?.warehouse?.warehouse_id) return;
    dispatch(fetchWarehouseDropdown());
  }, [dispatch]);


  // Handle edit action
  const handleEdit = (item: any) => {
    setEditTransferHistory(!editTransferHistory);
    setEdit({
      id: item.id,
      TransferHistoryName: item.TransferHistory_name,
      state: item.state_name,
      status: item.is_active,
      createdBy: item.created_by,
      updatedBy: item.updated_by,
      createDate: item.created_at,
      updatedDate: item.updated_at,
    });
  };
  const handlePackingClick = async (id: string) => {
    try {
      await WarehouseAxios.patch(`/stock/${id}/update-status/`, {
        issue_for_packing: true,
      });
      dispatch(
        OnfetchTransferRequest({
          page: 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search,
          filters: filterValue,
          wharehouse_id: selectedWarehouse?.warehouse?.warehouse_id,
        })
      );
      toast.success("Item Issued For Packing Successfully");
      // navigate('/warehouse-packing');
    } catch (error) {
      toast.error("Issue For Packing Failed !");
      console.error("API call failed:", error);
    }
  };

  const handleExport = async () => {
    try {
      const { blob, filename } = await exportTransferRequestCSV();

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export failed", error);
      toast.error("Failed to Export");
    }
  };

  const columns = useMemo(
    () => [
      {
        header: "Stock Transfer No",
        accessorKey: "stock_transfer_id",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/TransferRequest/dropdown/TransferHistory_name",
      },
      {
        header: "Transfer From",
        accessorKey: "transferred_from_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/TransferRequest/dropdown/TransferHistory_code",
      },

      {
        header: "Location Type",
        accessorKey: "warehouse_type",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/TransferRequest/dropdown/state_name",
      },
      {
        header: "Transfer To",
        accessorKey: "transferred_to_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/TransferRequest/dropdown/state_name",
      },
      {
        header: "Initiated By",
        accessorKey: "initiated_by",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/TransferRequest/dropdown/state_name",
      },
      {
        header: "Initiated Date",
        accessorKey: "transfer_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/TransferRequest/dropdown/state_name",
      },
      {
        header: "Approved By",
        accessorKey: "approved_by",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/TransferRequest/dropdown/approved_by",
      },
      {
        header: "Approved Date",
        accessorKey: "approved_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/TransferRequest/dropdown/approved_date",
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: "/TransferRequest/dropdown/state_name",
        cell: ({ row }: { row: any }) => {
          const status: string = row.original.status;

          const getStatusColor = (status: string) => {
            switch (status?.toLowerCase()) {
              case "approved":
                return Colors?.bg_green; // green
              case "rejected":
                return Colors?.bg_damage; // red
              case "pending":
                // case "approval pending":
                return Colors?.bg_yellow; // yellow
              default:
                return "#ccc"; // fallback gray
            }
          };
          return (
            <span
              className="badge d-inline-flex align-items-center gap-2 text-uppercase"
              style={{
                borderRadius: "10px",
                backgroundColor: getStatusColor(status),
                color: "#fff",
                fontWeight: 500,
              }}
            >
              <span
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  backgroundColor: "#fff",
                  display: "inline-block",
                }}
              />
              {status || "-"}
            </span>
          );
        },
      },
      {
        header: "Remarks",
        accessorKey: "remarks",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/TransferRequest/dropdown/state_name",
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;
          const conditionalArray = [];
          if (
            project_warehouse_permissions?.permissions?.View === true
          ) {
            conditionalArray.push({
              label: "View Details",
              icon: <InfoIcon fontSize="small" />,
              // to: `/warehouse-allocation-view/${data?.id}`,
              click: () =>
                navigate(`/view-transfer-req-details/${data.id}`),
            });
          }
          if (row.original.status == "Approved") {
            conditionalArray.push({
              label: "Issue Packing",
              icon: <ViewInArIcon fontSize="small" />,
              click: () => handlePackingClick(data.id),
            });
          }
          return (
            <ActionDropdown
              PropMinWidth="150px"
              rowIndex={row.index}
              options={[
                ...conditionalArray,
              ]}
            />
          );
        },
      },

      // {
      //   header: "Action",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => {
      //     const data = row.original;

      //     return (
      //       <Dropdown className="position-static">
      //         <Dropdown.Toggle
      //           bsPrefix="btn btn-subtle-secondary btn-sm btn-icon"
      //           role="button"
      //           data-bs-toggle="dropdown"
      //           aria-expanded="false"
      //         >
      //           <i className="bi bi-three-dots-vertical"></i>
      //         </Dropdown.Toggle>

      //         <Dropdown.Menu as="ul">
      //           <Dropdown.Item
      //             as={Link}
      //             to={`/view-transfer-req-details/${data.id}`}
      //           >
      //             <i className="ph-eye align-middle me-1"></i> View Details
      //           </Dropdown.Item>

      //           {row.original.status == "Approved" ? (
      //             <Dropdown.Item onClick={() => handlePackingClick(data.id)}>
      //               <i className="ph-pencil align-middle me-1"></i> Issue
      //               Packing
      //             </Dropdown.Item>
      //           ) : (
      //             <></>
      //           )}

      //           {/* <Dropdown.Item as={Link} to={`/completed/${data.id}`}>
      //             <i className="ph-trash align-middle me-1"></i> Completed
      //           </Dropdown.Item> */}
      //         </Dropdown.Menu>
      //       </Dropdown>
      //     );
      //   },
      // },
    ],
    [dispatch, TransferHistorys, project_warehouse_permissions?.permissions]
  );
  function handleChange(e: any): void {
    setSearchInput(e.target.value);
  }

  return (
    <Fragment>
      <div className="page-content">
        <Container fluid>
          <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2 py-3">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Warehouse</h6>
                    </Col>
                    <Col xl={2} md={3}>
                      <div className="search-box">
                        <Form.Control
                          type="text"
                          className="search"
                          placeholder="Search"
                          onChange={(e: any) => handleChange(e)}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col className="col-md-auto">
                      <div style={{ minWidth: "200px" }}>
                        <Autocomplete
                          options={warehouseDropdownList}
                          loading={warehouseDropdownLoading}
                          getOptionLabel={(option: any) =>
                            option?.warehouse?.warehouse_name
                          }
                          value={selectedWarehouse}
                          onChange={(event, selected) => {
                            dispatch(setSelectedWarehouse(selected));
                          }}
                          disableClearable
                          sx={{ ...CustomStyle?.singleselect, minHeight: 38 }}
                          renderInput={(params) => (
                            <BootstrapTextField
                              {...params}
                              name="transfer_from"
                              placeholder="Select Warehouse"
                              fullWidth
                              size="small"
                            />
                          )}
                        />
                      </div>
                    </Col>
                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        {project_warehouse_permissions?.permissions?.View ===
                          true && (
                            <div
                              className="hstack gap-2 btn btn-light"
                              onClick={handleExport}
                              style={{ cursor: "pointer" }}
                            >
                              <i className="bi bi-box-arrow-up align-baseline me-1"></i>{" "}
                              Export
                            </div>
                          )}
                        {/* <Link to="/add-item" className="btn btn-primary">
                          <i className="bi bi-plus-circle align-baseline me-1"></i>{" "}
                          Add DailyLog
                        </Link> */}
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <div className="d-flex gap-3 mt-3">
                      {/* <Link
                        to="/warehouse-daily-log"
                        className={`btn px-3 py-1 ${
                          currentPath === "/warehouse-daily-log"
                            ? "btn-primary"
                            : "btn-light btn-soft-secondary text-dark border"
                        }`}
                      >
                        Daily Log
                      </Link>

                      <Link
                        to="/warehouse-in-stock"
                        className={`btn px-3 py-1 ${
                          currentPath === "/warehouse-in-stock"
                            ? "btn-primary"
                            : "btn-light btn-soft-secondary text-dark border"
                        }`}
                      >
                        In Stock
                      </Link>

                      <Link
                        to="/warehouse-transfer-request"
                        className={`btn px-3 py-1 ${
                          currentPath === "/warehouse-transfer-request"
                            ? "btn-primary"
                            : "btn-light btn-soft-secondary text-dark border"
                        }`}
                      >
                        Transfer Request
                      </Link>

                      <Link
                        to="/warehouse-damage"
                        className={`btn px-3 py-1 ${
                          currentPath === "/warehouse-damage"
                            ? "btn-primary"
                            : "btn-light btn-soft-secondary text-dark border"
                        }`}
                      >
                        Damage
                      </Link> */}

                      <Link
                        to="/warehouse-daily-log"
                        style={{ textDecoration: "none" }}
                      >
                        <Button
                          size="sm"
                          className="px-3 py-1 border"
                          style={
                            currentPath === "/warehouse-daily-log"
                              ? {
                                backgroundColor: "#3F7DDA",
                                border: "1px solid #d6d8de",
                                color: "#fff",
                              }
                              : {
                                backgroundColor: "#eef0f7",
                                border: "1px solid #d6d8de",
                                color: "#000",
                              }
                          }
                        >
                          Daily Log
                        </Button>
                      </Link>

                      <Link
                        to="/warehouse-in-stock"
                        style={{ textDecoration: "none" }}
                      >
                        <Button
                          size="sm"
                          className="px-3 py-1 border"
                          style={
                            currentPath === "/warehouse-in-stock"
                              ? {
                                backgroundColor: "#3F7DDA",
                                border: "1px solid #d6d8de",
                                color: "#fff",
                              }
                              : {
                                backgroundColor: "#eef0f7",
                                border: "1px solid #d6d8de",
                                color: "#000",
                              }
                          }
                        >
                          In Stock
                        </Button>
                      </Link>

                      <Link
                        to="/warehouse-transfer-request"
                        style={{ textDecoration: "none" }}
                      >
                        <Button
                          size="sm"
                          className="px-3 py-1 border"
                          style={
                            currentPath === "/warehouse-transfer-request"
                              ? {
                                backgroundColor: "#3F7DDA",
                                border: "1px solid #d6d8de",
                                color: "#fff",
                              }
                              : {
                                backgroundColor: "#eef0f7",
                                border: "1px solid #d6d8de",
                                color: "#000",
                              }
                          }
                        >
                          Transfer Request
                        </Button>
                      </Link>

                      <Link
                        to="/warehouse-damage"
                        style={{ textDecoration: "none" }}
                      >
                        <Button
                          size="sm"
                          className="px-3 py-1 border"
                          style={
                            currentPath === "/warehouse-damage"
                              ? {
                                backgroundColor: "#3F7DDA",
                                border: "1px solid #d6d8de",
                                color: "#fff",
                              }
                              : {
                                backgroundColor: "#eef0f7",
                                border: "1px solid #d6d8de",
                                color: "#000",
                              }
                          }
                        >
                          Damage
                        </Button>
                      </Link>
                    </div>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  {loading ? (
                    <div className="text-center py-4">
                      <h5>Loading...</h5>
                    </div>
                  ) : error ? (
                    <div className="">
                      <WarehouseTableContainer
                        columns={columns}
                        data={data}
                        totalRecords={rowCount}
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        onPageChange={setPageIndex}
                        onPageSizeChange={setPageSize}
                        columnFilters={columnFilters}
                        setColumnFilters={setColumnFilters}
                        colValue={setColValue}
                        sorting={sorting}
                        onSortingChange={setSorting}
                        manualSorting={true}
                        isBordered={false}
                        isPagination={true}
                        isGlobalFilter={false}
                        SearchPlaceholder="Search Items..."
                      />
                    </div>
                  ) : (
                    <WarehouseTableContainer
                      columns={columns}
                      data={data}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      onPageChange={setPageIndex}
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      colValue={setColValue}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      manualSorting={true}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      //filterOptions={filterOptions}
                      SearchPlaceholder="Search Items..."
                    />
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default TransferRequest;
